import React, { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { DebitNoteForm } from './DebitNoteForm'
import { DebitNoteFormValues } from './schema'
import { getVoucher, listVoucherTypes } from '@/app/services/voucherService'
import { listSites } from '@/app/services/siteService'
import { listItems } from '@/app/services/itemService'
import { listGodowns } from '@/app/services/inventoryService'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { createDebitNote, updateDebitNote } from '@/app/services/debitNoteService'
import { VoucherResponse, VoucherCreate, VoucherUpdate } from '@/app/types/voucher'
import { Item } from '@/app/types/item'
import { VOUCHER_CODE_DEBIT_NOTE } from '@/app/lib/constants'

console.log('[DebitNoteDetailPage] File loaded')

const DebitNoteDetailPage: React.FC = () => {
  console.log('[DebitNoteDetailPage] Component function called')
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const fromInvoiceId = searchParams.get('fromInvoiceId')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditMode = !!id

  console.log('[DebitNoteDetailPage] Mounted with:', { id, fromInvoiceId, isEditMode })

  const [isSearchingItems, setIsSearchingItems] = useState(false)

  // Fetch voucher types to find the DNOT type
  const { data: voucherTypesData, isLoading: isLoadingVoucherTypes } = useQuery({
    queryKey: ['voucher-types'],
    queryFn: () => listVoucherTypes({ limit: 100 })
  })

  // Find the Debit Note voucher type
  const debitNoteVoucherType = voucherTypesData?.items?.find(
    (type) => type.code === VOUCHER_CODE_DEBIT_NOTE
  )
  const debitNoteVoucherTypeId = debitNoteVoucherType?.id

  // Fetch original Purchase Invoice if 'fromInvoiceId' is present
  const { data: originalInvoice, isLoading: isLoadingOriginalInvoice } = useQuery({
    queryKey: ['originalPurchaseInvoice', fromInvoiceId],
    queryFn: () => (fromInvoiceId ? getVoucher(Number(fromInvoiceId)) : Promise.resolve(null)),
    enabled: !!fromInvoiceId && !isEditMode // Only fetch if creating new and fromInvoiceId exists
  })

  // Fetch Debit Note if in edit mode
  const {
    data: debitNote,
    isLoading: isLoadingDebitNote,
    isError: isErrorDebitNote,
    error: errorDebitNote
  } = useQuery({
    queryKey: ['debitNote', id],
    queryFn: () => (id ? getVoucher(Number(id)) : Promise.resolve(null)),
    enabled: isEditMode
  })

  // Fetch common data (sites, godowns)
  const { data: sitesData = { items: [] } } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 })
  })

  const { data: godownsData = { items: [] } } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 })
  })

  // Function to search items for product selection in the grid
  const searchItems = async (query: string): Promise<Item[]> => {
    if (!query || query.length < 2) return []

    setIsSearchingItems(true)
    try {
      const result = await listItems({ search_query: query, limit: 20 })
      return result.items || []
    } catch (error) {
      console.error('Error searching items:', error)
      return []
    } finally {
      setIsSearchingItems(false)
    }
  }

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['debitNotes'] })
      queryClient.invalidateQueries({ queryKey: ['debitNote', id] })
      if (fromInvoiceId) {
        queryClient.invalidateQueries({ queryKey: ['purchase-invoice', fromInvoiceId] })
      }
      toast.success(`Debit Note ${isEditMode ? 'updated' : 'created'} successfully.`)
      navigate('/debit-notes/list')
    },
    onError: (error: Error) => {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} Debit Note: ${error.message}`)
    }
  }

  const createDebitNoteMutation = useMutation({
    mutationFn: (data: VoucherCreate) => createDebitNote(data),
    ...mutationOptions
  })

  const updateDebitNoteMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VoucherUpdate }) => updateDebitNote(id, data),
    ...mutationOptions
  })

  const handleSubmit = (formData: DebitNoteFormValues) => {
    // Format the data for API
    const payload = {
      ...formData,
      voucher_type_id: debitNoteVoucherTypeId!,
      // Format dates as strings for API
      transaction_date:
        formData.transaction_date instanceof Date
          ? formData.transaction_date.toISOString().split('T')[0]
          : formData.transaction_date,
      is_cancelled: false
    }

    // If creating from an original invoice, add reference_invoice_number to payload
    if (!isEditMode && fromInvoiceId && originalInvoice) {
      console.log(
        '[DebitNoteDetailPage] Adding reference_invoice_number:',
        originalInvoice.voucher_number
      )
      ;(payload as VoucherCreate & { reference_invoice_number?: string }).reference_invoice_number =
        originalInvoice.voucher_number
    }

    if (isEditMode && id) {
      updateDebitNoteMutation.mutate({ id: Number(id), data: payload as unknown as VoucherUpdate })
    } else {
      createDebitNoteMutation.mutate(payload as unknown as VoucherCreate)
    }
  }

  const isLoading =
    isLoadingVoucherTypes ||
    (isEditMode && isLoadingDebitNote) ||
    (!isEditMode && !!fromInvoiceId && isLoadingOriginalInvoice)
  const isSubmitting = createDebitNoteMutation.isPending || updateDebitNoteMutation.isPending

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!debitNoteVoucherTypeId) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Debit Note voucher type not found. Please create a voucher type with code "
          {VOUCHER_CODE_DEBIT_NOTE}" first.
        </AlertDescription>
      </Alert>
    )
  }

  if (isErrorDebitNote && isEditMode) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Error loading debit note: {(errorDebitNote as Error)?.message}
        </AlertDescription>
      </Alert>
    )
  }

  // Determine initial data for the form
  let initialData: Partial<VoucherResponse> | undefined = undefined
  if (isEditMode && debitNote) {
    initialData = debitNote
  } else if (!isEditMode && originalInvoice) {
    // Use the original invoice number directly as the reference number
    const referenceNumber = originalInvoice.voucher_number

    // Log for debugging
    console.log('[DebitNoteDetailPage] Original voucher_number:', originalInvoice.voucher_number)
    console.log('[DebitNoteDetailPage] Using reference number:', referenceNumber)

    // Pre-fill from original invoice
    initialData = {
      party_id: originalInvoice.party_id,
      site_id: originalInvoice.site_id,
      reference_number: referenceNumber,
      // Add reference_invoice_number to store the original invoice number for backend
      reference_invoice_number: originalInvoice.voucher_number,
      // Use reference_id to track original invoice id internally
      reference_id: originalInvoice.id,
      items:
        originalInvoice.items?.map((item) => ({
          ...item,
          id: undefined, // This is a new debit note item
          // Store the original invoice item ID as a custom field
          original_invoice_item_id: item.id,
          quantity: item.quantity // User will adjust this as needed
        })) || [],
      // Set current date
      transaction_date: new Date().toISOString()
    } as Partial<VoucherResponse> & { reference_invoice_number?: string; reference_id?: number }

    // Log to ensure we're passing party_id correctly
    console.log('[DebitNoteDetailPage] Initialized with party_id:', initialData.party_id)
    console.log('[DebitNoteDetailPage] Set reference number:', referenceNumber)
  } else {
    // Creating a new debit note without original invoice
    console.log('[DebitNoteDetailPage] Creating new debit note without original invoice')
    initialData = undefined
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(isEditMode ? '/debit-notes/list' : '/purchase-invoices/list')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? `Edit Debit Note ${debitNote?.voucher_number}` : 'Create Debit Note'}
            {fromInvoiceId &&
              !isEditMode &&
              originalInvoice &&
              ` for Invoice ${originalInvoice.voucher_number}`}
          </h1>
        </div>
      </div>

      {fromInvoiceId && originalInvoice && !isEditMode && (
        <Alert>
          <AlertDescription>
            Creating debit note for Purchase Invoice #{originalInvoice.voucher_number} from{' '}
            {originalInvoice.party?.name}. Vendor and site details are automatically populated.
          </AlertDescription>
        </Alert>
      )}

      {console.log('[DebitNoteDetailPage] Rendering form with:', {
        hasInitialData: !!initialData,
        sitesCount: sitesData.items?.length || 0,
        godownsCount: godownsData.items?.length || 0,
        debitNoteVoucherTypeId,
        hasOriginalInvoice: !!originalInvoice
      })}
      {godownsData.items && godownsData.items.length === 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No warehouses found. Please create at least one warehouse before creating debit notes.
          </AlertDescription>
        </Alert>
      )}
      
      {godownsData.items && godownsData.items.length > 0 && (
        <DebitNoteForm
          debitNote={initialData}
          sites={sitesData.items || []}
          godowns={godownsData.items || []}
          voucherTypeId={debitNoteVoucherTypeId}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          searchItems={searchItems}
          isLoadingItems={isSearchingItems}
          originalInvoiceItems={originalInvoice?.items || []}
          originalInvoiceParty={originalInvoice?.party}
        />
      )}
    </div>
  )
}

export default DebitNoteDetailPage
